import { CLIENT_STATE_KEYS } from '@thoughtspot/client-state-util';
import { getDataFormatter, getFormattedValueForNumericColumn, getYAxisLabelForAttribute, } from '@thoughtspot/formatter-service';
import { getId, getMetricsDefinition, getSourceName, isColumnValidForNumberFormatting, isEffectivelyNumeric, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getClientLegendPosition, getVerticalPositionedLegend, } from '../components/chart-legend-utils';
import { getColumnConfig, getLegendCardinality, getSeries, getTooltipConfig, getXAxisLabelAt, } from '../data/data-util';
import { getEnvFlags, getTranslatedTextCallback } from '../env';
import GeoConfig from '../geomap/base/geo-config';
import { ChartResponsiveLayout, ChartType, } from '../types';
import { createLogger } from '../utils/logger';
import { getChartProperties, getChartProperty, getChartType, getColumn, getGeoObjects, getRadialColumn, getXAxisColumns, getXAxisFilterdWithTooltipEnabled, getYAxes, getYAxisColumns, isTimeSeries, } from './chart-model-utils';
import { configOptions, getChartBasedGridLineConfig, hasPersistentLegend, isGeoMapChartType, } from './chart-type-specification-service';
import { getName } from './column-name-util';
import { getGeoConfig } from './geo-column-utils';
const logger = createLogger('chart-ui-utils');
export const formattingConstants = {
    EXPECTED_WIDTH_PER_Y_AXIS: 15,
    MIN_WIDTH_TO_SHOW_LEGEND: 300,
    MIN_HEIGHT_TO_SHOW_LEGEND: 200,
    MIN_WIDTH_TO_SHOW_VERTICAL_LEGEND: 400,
    COMPOSITE_COLUMN_VALUES_JOIN_KEY: ', ',
    HORIZONTAL_LEGEND_MAX_CARDINALITY: 5,
    MIN_WIDTH_TO_SHOW_HIGHCHART_TITLE: 400,
    TO_SHOW_DATA_LABEL: { MIN_WIDTH: 300, MIN_HEIGHT: 200 },
    SHOW_X_AXIS_NAME: {
        MIN_X: { WIDTH: 300, HEIGHT: 100 },
        MIN_Y: { WIDTH: 100, HEIGHT: 200 },
    },
    SHOW_Y_AXIS_NAME: {
        MIN_X: { WIDTH: 300, HEIGHT: 100 },
        MIN_Y: { WIDTH: 100, HEIGHT: 200 },
    },
    SHOW_X_AXIS_LABEL: {
        MIN_X: { WIDTH: 300, HEIGHT: 100 },
        MIN_Y: { WIDTH: 100, HEIGHT: 200 },
    },
    SHOW_Y_AXIS_LABEL: {
        MIN_X: { WIDTH: 300, HEIGHT: 100 },
        MIN_Y: { WIDTH: 100, HEIGHT: 200 },
    },
};
export var LegendOrientation;
(function (LegendOrientation) {
    LegendOrientation["Horizontal"] = "HORIZONTAL";
    LegendOrientation["Vertical"] = "VERTICAL";
})(LegendOrientation || (LegendOrientation = {}));
export var GeoConfigType;
(function (GeoConfigType) {
    GeoConfigType["LATITUDE"] = "LATITUDE";
    GeoConfigType["LONGITUDE"] = "LONGITUDE";
    GeoConfigType["ZIP_CODE"] = "ZIP_CODE";
    GeoConfigType["ADMIN_DIV_0"] = "ADMIN_DIV_0";
    GeoConfigType["ADMIN_DIV_1"] = "ADMIN_DIV_1";
    GeoConfigType["ADMIN_DIV_2"] = "ADMIN_DIV_2";
    GeoConfigType["CUSTOM_REGION"] = "CUSTOM_REGION";
    GeoConfigType["POINT"] = "POINT";
})(GeoConfigType || (GeoConfigType = {}));
export const getDefaultLabelEnablementValues = () => {
    return {
        shouldShowXAxisLabels: true,
        shouldShowYAxisLabels: true,
        shouldShowXAxisName: true,
        shouldShowYAxisName: true,
    };
};
export function getGridLinesConfig(chartModel, enableGridline) {
    var _a, _b, _c, _d, _e, _f;
    const chartType = getChartType(chartModel);
    const chartConfigOptions = configOptions(chartType, chartModel);
    const enabledResponsiveLayout = ((_b = (_a = chartModel.vizProp) === null || _a === void 0 ? void 0 : _a.chartProperties) === null || _b === void 0 ? void 0 : _b.responsiveLayoutPreference) ===
        ChartResponsiveLayout.AutoOn ||
        ((_d = (_c = chartModel.vizProp) === null || _c === void 0 ? void 0 : _c.chartProperties) === null || _d === void 0 ? void 0 : _d.responsiveLayoutPreference) ===
            ChartResponsiveLayout.UserPreferredOn;
    let defaultGridLinesConfig;
    if (enableGridline &&
        _.isNil((_f = (_e = chartModel.vizProp) === null || _e === void 0 ? void 0 : _e.chartProperties) === null || _f === void 0 ? void 0 : _f.gridLines) &&
        !enabledResponsiveLayout &&
        !isTimeSeries(chartModel)) {
        defaultGridLinesConfig = getChartBasedGridLineConfig(chartType, chartModel.config.axisConfig);
    }
    else {
        defaultGridLinesConfig = _.get(chartConfigOptions, 'allowedConfigurations.gridLineConfig', {});
    }
    const { gridLines } = getChartProperties(chartModel);
    return {
        xGridlineEnabled: _.isNil(gridLines === null || gridLines === void 0 ? void 0 : gridLines.xGridlineEnabled)
            ? defaultGridLinesConfig.xGridlineEnabled
            : gridLines.xGridlineEnabled,
        yGridlineEnabled: _.isNil(gridLines === null || gridLines === void 0 ? void 0 : gridLines.yGridlineEnabled)
            ? defaultGridLinesConfig.yGridlineEnabled
            : gridLines.yGridlineEnabled,
    };
}
export const canShowHighchartTitle = (shouldShowHighchartTitle, vizContainerWidth, chartModel) => {
    if (shouldShowHighchartTitle === false) {
        return false;
    }
    const chartConfigOption = configOptions(chartModel.config.chartType, chartModel);
    if (!chartConfigOption.showHighchartTitle) {
        return false;
    }
    return !!(vizContainerWidth >=
        formattingConstants.MIN_WIDTH_TO_SHOW_HIGHCHART_TITLE);
};
export const hasEnoughSpaceForDataLabels = (vizContainer, chartType) => {
    const { width, height } = vizContainer;
    const { MIN_WIDTH, MIN_HEIGHT } = formattingConstants.TO_SHOW_DATA_LABEL;
    return ([ChartType.Pie, ChartType.Funnel].includes(chartType) ||
        (width >= MIN_WIDTH && height >= MIN_HEIGHT));
};
export const shouldShowPropertyBasedOnDimension = (vizContainer, visibilityLimit) => {
    if ((vizContainer === null || vizContainer === void 0 ? void 0 : vizContainer.width) === undefined || (vizContainer === null || vizContainer === void 0 ? void 0 : vizContainer.height) === undefined)
        return true;
    const { MIN_X, MIN_Y } = visibilityLimit;
    const { height, width } = vizContainer;
    return ((height >= MIN_X.HEIGHT && width >= MIN_X.WIDTH) ||
        (height >= MIN_Y.HEIGHT && width >= MIN_Y.WIDTH));
};
export const isChartBigEnoughToShowLegend = (isLegendVertical, containerDimensions) => {
    if (isLegendVertical) {
        return (containerDimensions.width >=
            formattingConstants.MIN_WIDTH_TO_SHOW_LEGEND);
    }
    return (containerDimensions.height >=
        formattingConstants.MIN_HEIGHT_TO_SHOW_LEGEND);
};
export const isLegendVertical = (isGeoMapChart, chartDimensions, legendCardinality, isKpiChartType, clientLegendPosition) => {
    return ((chartDimensions.width >=
        formattingConstants.MIN_WIDTH_TO_SHOW_VERTICAL_LEGEND ||
        legendCardinality >
            formattingConstants.HORIZONTAL_LEGEND_MAX_CARDINALITY ||
        isGeoMapChart ||
        isKpiChartType) &&
        (_.isNil(clientLegendPosition) ||
            getVerticalPositionedLegend().includes(clientLegendPosition)));
};
export const getLegendOrientation = (chartModel, chartDimensions, dataModel) => {
    var _a;
    const legendCardinality = getLegendCardinality(chartModel, dataModel);
    const chartType = (_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.config) === null || _a === void 0 ? void 0 : _a.chartType;
    const _isGeoMapChartType = isGeoMapChartType(chartType);
    const _isKpiChartType = chartType === ChartType.Kpi;
    const shouldShowLegendVertical = isLegendVertical(_isGeoMapChartType, chartDimensions, legendCardinality, _isKpiChartType, getClientLegendPosition(chartModel));
    return shouldShowLegendVertical
        ? LegendOrientation.Vertical
        : LegendOrientation.Horizontal;
};
export const showLegend = (chartModel, dataModel) => {
    const chartConfigOption = configOptions(chartModel.config.chartType, chartModel);
    if (chartConfigOption.doNotShowLegend) {
        return false;
    }
    if (hasPersistentLegend(getChartType(chartModel))) {
        return false;
    }
    return !(getLegendCardinality(chartModel, dataModel) <= 1);
};
export function canShowLegend(chartModel, dataModel, chartDimensions, isLegendVrtical) {
    const legendVisibility = getChartProperty(chartModel, CLIENT_STATE_KEYS.showLegend);
    if (!chartModel ||
        !showLegend(chartModel, dataModel) ||
        !(_.isNil(legendVisibility) || legendVisibility)) {
        return false;
    }
    return isChartBigEnoughToShowLegend(isLegendVrtical, chartDimensions);
}
export function getTooltipDataNameValuePairs(tooltipDataContent, chartModel) {
    var _a;
    const tooltipConfigColumnIds = ((_a = getTooltipConfig(chartModel)) === null || _a === void 0 ? void 0 : _a.columnIds) || [];
    const tooltipDataNameValuePairs = {};
    tooltipConfigColumnIds.forEach((colId) => {
        const tooltipDataForColId = tooltipDataContent[colId];
        if (tooltipDataForColId) {
            const { column, dataValue } = tooltipDataForColId;
            const chartModelColumn = getColumn(chartModel, colId);
            const value = isColumnValidForNumberFormatting(chartModelColumn)
                ? getFormattedValueForNumericColumn(dataValue, chartModelColumn)
                : getDataFormatter(chartModelColumn)(dataValue);
            tooltipDataNameValuePairs[colId] = {
                name: getName(column),
                value,
            };
        }
    });
    return tooltipDataNameValuePairs;
}
export function getGeoChartData(chartModel, dataModel, getTranslatedText) {
    let geoConfig;
    const xAxisColumns = getXAxisColumns(chartModel);
    switch (xAxisColumns.length) {
        case 1:
            geoConfig = getGeoConfig(xAxisColumns[0]);
            break;
        case 2:
            geoConfig = new GeoConfig({
                type: GeoConfigType.POINT,
            });
            break;
        default:
            logger.error('<=2 x-axis columns expected for geo charts');
            return;
    }
    const series = getSeries(dataModel);
    const yAxisColumns = getYAxisColumns(chartModel);
    const multipleYAxes = getYAxes(chartModel).length > 1;
    _.each(yAxisColumns, (yAxisColumn, columnIndex) => {
        const metricDefinition = getMetricsDefinition(yAxisColumn);
        if (!metricDefinition) {
            return;
        }
        const serie = multipleYAxes ? series[columnIndex] : series[0];
        _.each(serie.data, dataRow => {
            const color = metricDefinition.getColorForValue(dataRow.y);
            if (color !== null) {
                dataRow.color = color;
            }
            else {
                delete dataRow.color;
            }
        });
    });
    const geoObjects = getGeoObjects(chartModel);
    const customNumberFormatPatternForGeoChart = getEnvFlags()
        .customNumberFormatPatternForGeoChart;
    return {
        geoConfig,
        series,
        geoObjects,
        labelFormatters: {
            x: (xValue, seriesIndex, filterTooltipColumnsWithTooltipConfig = false) => {
                let xAxisColumns = getXAxisColumns(chartModel);
                if (filterTooltipColumnsWithTooltipConfig)
                    xAxisColumns = getXAxisFilterdWithTooltipEnabled(chartModel, xAxisColumns);
                return {
                    name: _.map(xAxisColumns, col => getName(col)).join(', '),
                    value: getXAxisLabelAt(chartModel, dataModel, xValue, undefined, undefined, getTranslatedText, filterTooltipColumnsWithTooltipConfig),
                };
            },
            y: (yValue, seriesIndex) => {
                const yAxisColumns = getYAxisColumns(chartModel);
                const yAxisColumn = yAxisColumns[seriesIndex] || yAxisColumns[0];
                let formattedValue = null;
                if (isEffectivelyNumeric(yAxisColumn)) {
                    if (!_.isNil(customNumberFormatPatternForGeoChart) &&
                        !_.isEmpty(customNumberFormatPatternForGeoChart) &&
                        !_.isNil(yAxisColumn.format)) {
                        yAxisColumn.format.pattern = customNumberFormatPatternForGeoChart;
                    }
                    formattedValue = getFormattedValueForNumericColumn(yValue, yAxisColumn);
                }
                else {
                    formattedValue = getYAxisLabelForAttribute(getColumnConfig(yAxisColumn, chartModel, dataModel), yValue, yValue);
                }
                return {
                    name: getName(yAxisColumn),
                    value: formattedValue,
                };
            },
            z: (zValue, seriesIndex) => {
                const radialColumn = getRadialColumn(chartModel);
                if (!radialColumn) {
                    return null;
                }
                return {
                    name: getName(radialColumn),
                    value: getDataFormatter(radialColumn)(zValue),
                };
            },
        },
        tooltipDataFormatter: (tooltipDataContent) => getTooltipDataNameValuePairs(tooltipDataContent, chartModel),
    };
}
export function getColumnIdToDisambiguatedName(columns, plainText = false, getTranslatedText, overrideColumnNames = {}) {
    const columnNameToColumnIndices = {};
    columns.forEach((column, index) => {
        const columnId = getId(column);
        const thisColumnName = overrideColumnNames[columnId] || getName(column);
        if (!_.has(columnNameToColumnIndices, thisColumnName)) {
            columnNameToColumnIndices[thisColumnName] = [];
        }
        columnNameToColumnIndices[thisColumnName].push(index);
    });
    const columnIdToDisambiguatedName = {};
    let uniqueNameTemplate = '{1} - <span class="bk-axis-column-source">in {2}</span>';
    if (plainText) {
        uniqueNameTemplate = '{1} - in {2}';
    }
    _.forIn(columnNameToColumnIndices, (columnIndices, columnName) => {
        if (columnIndices.length === 1) {
            const column = columns[columnIndices[0]];
            columnIdToDisambiguatedName[getId(column)] = columnName;
        }
        else {
            columnIndices.forEach(columnIndex => {
                const column = columns[columnIndex];
                const uniqueName = getTranslatedText(uniqueNameTemplate, {
                    1: columnName,
                    2: getSourceName(column, getTranslatedTextCallback()),
                });
                columnIdToDisambiguatedName[getId(column)] = uniqueName;
            });
        }
    });
    return columnIdToDisambiguatedName;
}
//# sourceMappingURL=chart-ui-utils.js.map